import React from 'react';
import { Button, ConfigProvider, Image, Space, Tooltip, notification } from 'antd';
import { 
  CopyOutlined, 
  DownloadOutlined, 
  SwapOutlined, 
  RotateLeftOutlined, 
  RotateRightOutlined, 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  FileOutlined 
} from '@ant-design/icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import bot_icon from '../icons/bot-icon.png';

interface Message {
  sender: 'agent' | 'human' | 'system';
  text: string;
  videoTracked?: boolean;
}

interface ChatMessageProps {
  message: Message;
  index: number;
  onDownload: (url: string) => void;
  extractAllImages: (text: string) => string[];
  extractAllVideos: (text: string) => string[];
  extractAllFiles: (text: string) => string[];
  removeMediaUrls: (text: string) => string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  index,
  onDownload,
  extractAllImages,
  extractAllVideos,
  extractAllFiles,
  removeMediaUrls
}) => {
  const extractedImages = extractAllImages(message.text);
  const extractedVideos = extractAllVideos(message.text);
  const extractedFiles = extractAllFiles(message.text);
  const cleanTextContent = removeMediaUrls(message.text);

  const renderImagePreview = (imgUrl: string, imgIndex: number, isAgent: boolean = true) => (
    <div key={imgIndex} className="agent-image-container">
      <Image
        src={imgUrl}
        alt={isAgent ? `Generated ${imgIndex + 1}` : "Uploaded"}
        style={{ marginTop: '10px', maxWidth: '100%', borderRadius: '10px' }}
        preview={{
          toolbarRender: (_, { actions }) => (
            <Space size={16} className="toolbar-wrapper">
              <DownloadOutlined
                style={{ color: 'white', fontSize: '30px' }}
                onClick={() => onDownload(imgUrl)}
              />
              <SwapOutlined 
                rotate={90} 
                onClick={actions.onFlipY}
                style={{ fontSize: '30px' }} 
              />
              <SwapOutlined 
                onClick={actions.onFlipX}
                style={{ fontSize: '30px' }} 
              />
              <RotateLeftOutlined 
                onClick={actions.onRotateLeft}
                style={{ fontSize: '30px' }} 
              />
              <RotateRightOutlined 
                onClick={actions.onRotateRight}
                style={{ fontSize: '30px' }} 
              />
              <ZoomOutOutlined 
                onClick={actions.onZoomOut}
                style={{ fontSize: '30px' }} 
              />
              <ZoomInOutlined 
                onClick={actions.onZoomIn}
                style={{ fontSize: '30px' }} 
              />
            </Space>
          ),
          maskClassName: "image-preview-mask"
        }}
      />
    </div>
  );

  const renderVideoPreview = (videoUrl: string, videoIndex: number) => (
    <div key={videoIndex} className="agent-video-container">
      <video 
        key={videoUrl} 
        autoPlay 
        preload="auto" 
        muted 
        loop 
        playsInline
        controls
        style={{ marginTop: '10px', maxWidth: '100%', borderRadius: '10px' }}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );

  const renderFilePreview = (fileName: string, fileIndex: number, isAgent: boolean = true) => (
    <div key={fileIndex} className="agent-file-container">
      <div className="file-attachment" style={{ 
        padding: '10px', 
        border: isAgent ? '1px solid #ddd' : '1px solid rgba(255,255,255,0.3)', 
        borderRadius: '8px', 
        marginTop: '10px',
        backgroundColor: isAgent ? '#f5f5f5' : 'rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FileOutlined style={{ 
            fontSize: '20px', 
            marginRight: '10px', 
            color: isAgent ? '#1890ff' : 'white' 
          }} />
          <div>
            <div style={{ 
              fontWeight: 'bold', 
              color: isAgent ? '#333' : 'white' 
            }}>
              {fileName || (isAgent ? 'Downloaded File' : 'Uploaded File')}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: isAgent ? '#666' : 'rgba(255,255,255,0.7)' 
            }}>
              {isAgent ? 'File uploaded to workspace' : 'Uploaded to workspace'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (message.sender === 'agent') {
    if (extractedImages.length > 0 || extractedVideos.length > 0 || extractedFiles.length > 0 || cleanTextContent) {
      return (
        <div key={index} className="chat-message agent-message">
          <img src={bot_icon} alt="Bot Icon" className="bot-icon" />
          <div className="message-content">
            <Markdown remarkPlugins={[remarkGfm]}>{cleanTextContent}</Markdown>
          
            <div>
              <Tooltip title="Copy text">
                <ConfigProvider theme={{ 
                  components: {
                    Button: {
                      defaultBg: 'transparent',
                      defaultColor: 'white',
                      defaultHoverBg: 'rgba(255, 255, 255, 0.1)',
                      defaultHoverColor: 'white',
                      colorBgBase: 'transparent',
                      linkHoverBg: 'rgba(255, 255, 255, 0.1)',
                      colorLink: 'white',
                      colorLinkHover: 'white',
                      colorLinkActive: 'rgba(255, 255, 255, 0.8)',
                    },
                  },
                  token: { colorPrimary: 'white' } 
                }}>
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(cleanTextContent)
                        .then(() => notification.success({ message: 'Copied to clipboard', duration: 2 }))
                        .catch(() => notification.error({ message: 'Failed to copy to clipboard', duration: 2 }));
                    }}
                  />
                </ConfigProvider>
              </Tooltip>
            </div>
            
            {extractedImages.map((imgUrl, imgIndex) => renderImagePreview(imgUrl, imgIndex, true))}
            {extractedVideos.map((videoUrl, videoIndex) => renderVideoPreview(videoUrl, videoIndex))}
            {extractedFiles.map((fileName, fileIndex) => renderFilePreview(fileName, fileIndex, true))}
          </div>
        </div>
      );
    }
  } else {
    return (
      <div key={index} className="chat-message human-message">
        <div style={{color: "white"}}>
          <Markdown remarkPlugins={[remarkGfm]}>{cleanTextContent}</Markdown>
        </div>
        {extractedImages.map((imgUrl, imgIndex) => renderImagePreview(imgUrl, imgIndex, false))}
        {extractedVideos.map((videoUrl, videoIndex) => renderVideoPreview(videoUrl, videoIndex))}
        {extractedFiles.map((fileName, fileIndex) => renderFilePreview(fileName, fileIndex, false))}
      </div>
    );
  }

  return null;
};

export default ChatMessage;
